<template>
	<div
		v-if="data && !data.error && filteredServices.length"
		class="flex break-normal leading-none cursor-pointer"
		:class="{
			'flex-row items-center flex-wrap gap-1': variant === 'ultra-compact',
			'flex-col gap-2': variant !== 'ultra-compact',
		}"
		@click="otherHoursAsAnchor ? goToAnchor() : (showModal = true)"
	>
		<div
			v-for="(service, index) of filteredServices"
			:key="index"
			class="flex flex-row items-center flex-wrap gap-1"
			:class="{'justify-center': centerContent}"
		>
			<span
				v-if="
					(service.name || (service.primary && categoryName)) &&
					(variant !== 'ultra-compact' || uniqueStatusesCount !== 1)
				"
				class="font-bold"
			>
				{{ getServiceName(service) }}
			</span>
			<span
				class="asitbh__status rounded uppercase font-black px-[8px] whitespace-nowrap"
				:class="{
					[getStatusData(service).cssClass]: true,
					'text-[10px] py-[3px]': variant !== 'ultra-compact' || uniqueStatusesCount !== 1,
					'text-[12px] py-[4px]': variant === 'ultra-compact' && uniqueStatusesCount === 1,
				}"
			>
				{{ getStatusData(service).label }}
			</span>
			<span
				v-if="
					!service.week[0].open &&
					service.change &&
					(variant !== 'ultra-compact' || uniqueStatusesCount === 1)
				"
				class="text-xs"
			>
				{{
					`${
						service.week[0].open
							? stringaMultilingua('Chiude', 'Closes', 'Schließt')
							: stringaMultilingua('Apre', 'Opens', 'Öffnet')
					} ${formatChange(service.change.date)}`
				}}
			</span>
			<n-button
				v-if="index === filteredServices.length - 1 && hasSomeHours"
				type="primary"
				size="tiny"
				icon-placement="right"
				text
				class="text-xs"
				:theme-overrides="{
					heightTiny: '',
					paddingTiny: '',
					iconMarginTiny: '0 0 0 1px',
					iconSizeTiny: '12px',
				}"
			>
				<template #icon>
					<n-icon>
						<i-carbon-chevron-right />
					</n-icon>
				</template>
				{{ stringaMultilingua('Orari', 'Hours', 'Fahrpläne') }}
			</n-button>
		</div>
		<n-modal
			v-model:show="showModal"
			preset="card"
			class="max-w-[500px]"
			:title="`${stringaMultilingua('Orari', 'Hours', 'Fahrpläne')} ${minisite.name}`"
		>
			<div class="flex flex-col gap-3">
				<n-collapse accordion v-model:expanded-names="collapseValue">
					<n-collapse-item v-for="(service, index) of services" :key="index" :name="index">
						<template #header>
							<n-text class="text-lg select-none" type="primary">
								{{ getServiceName(service) }}
							</n-text>
						</template>
						<n-table
							:bordered="false"
							size="small"
							:theme-overrides="{tdColor: 'transparent'}"
							:bottom-bordered="false"
						>
							<tbody>
								<tr
									v-for="(day, index) of service.week"
									:key="day.weekday"
									:class="{'font-semibold': index === 0}"
								>
									<td class="w-[100px]">{{ formatWeekDay(day.weekday) }}</td>
									<td>
										<bh-status-day :day="day" :format-hours="formatTableHours" :locale="locale" />
									</td>
								</tr>
							</tbody>
						</n-table>
					</n-collapse-item>
				</n-collapse>
			</div>
		</n-modal>
	</div>
</template>

<script setup>
	import {NCollapse, NCollapseItem, NTable, NModal, NButton, NIcon, NText} from 'naive-ui'
	import {DateTime} from 'luxon'
	import BhStatusDay from './BhStatusDay.vue'
	import {ref, toRefs, computed} from 'vue'
	import ICarbonChevronRight from '~icons/carbon/chevron-right'

	const props = defineProps({
		data: Object,
		minisite: Object,
		variant: {
			type: String,
			default: 'full',
		},
		locale: {
			type: String,
			default: 'it',
		},
		otherHoursAsAnchor: {
			type: Boolean,
			default: false,
		},
		centerContent: {
			type: Boolean,
			default: false,
		},
	})
	const {data, minisite, variant, locale} = toRefs(props)
	const language = computed(() => locale.value.split('-')[0])
	const now = DateTime.fromISO(data.value.relativeTo)
	const showModal = ref(false)
	const collapseValue = ref([0])
	const services = computed(() =>
		data.value.services.filter(service => (service.primary || service.name) && service.week?.length)
	)
	const uniqueStatusesCount = computed(
		() => [...new Set(services.value.map(service => getStatusData(service).cssClass))].length
	)
	const filteredServices = computed(() =>
		services.value.filter(service => {
			if (uniqueStatusesCount.value === 1 && variant.value === 'ultra-compact') {
				return service.primary
			}

			return true
		})
	)
	const categoryName = computed(() => minisite.value.name?.split(' ')?.[0])
	const hasSomeHours = computed(() =>
		services.value.some(service => service.week.some(day => day.open && day.hours?.length))
	)

	function formatWeekDay(value) {
		switch (value) {
			case 1:
				return stringaMultilingua('Lunedì', 'Monday', 'Montag')
			case 2:
				return stringaMultilingua('Martedì', 'Tuesday', 'Dienstag')
			case 3:
				return stringaMultilingua('Mercoledì', 'Wednesday', 'Mittwoch')
			case 4:
				return stringaMultilingua('Giovedì', 'Thursday', 'Donnerstag')
			case 5:
				return stringaMultilingua('Venerdì', 'Friday', 'Freitag')
			case 6:
				return stringaMultilingua('Sabato', 'Saturday', 'Samstag')
			case 7:
				return stringaMultilingua('Domenica', 'Sunday', 'Sonntag')
		}
	}

	function isOpenWeekend(service) {
		const friday = service.week.find(day => day.weekday === 5)
		function isOpenFridayEvening() {
			return (
				friday.open &&
				friday.hours?.length &&
				friday.hours.some(
					hour =>
						hour.opensAt.startsWith(`${friday.date}T`) && hour.opensAt.split('T')[1] >= '18:00'
				)
			)
		}

		// can be 3 days if today is sunday
		return (
			service.week.filter(day => [6, 7].includes(day.weekday)).every(day => day.open) &&
			service.week.filter(day => ![5, 6, 7].includes(day.weekday)).every(day => !day.open) &&
			(!friday.open || isOpenFridayEvening())
		)
	}

	function isPartiallyOpen(service) {
		return service.week.some(day => day.open) && service.week.some(day => !day.open)
	}

	function getServiceName(service) {
		return service.name || (service.primary ? categoryName.value : '')
	}

	function getStatusData(service) {
		let suffix, label, cssClass
		const scope = service.week[0].scope

		if (scope?.useAsStatus) {
			cssClass = `asitmiup-color--${scope.statusColor}`
			label = scope.name
		} else if (isOpenWeekend(service)) {
			suffix = 'open-weekend'
			label = stringaMultilingua('Aperto weekend', 'Open weekend', 'Wochenende geöffnet')
		} else if (isPartiallyOpen(service)) {
			suffix = 'open-partially'
			label = stringaMultilingua('Apertura parziale', 'Partial opening', 'Teilweise Öffnung')
		} else if (service.week[0].open) {
			suffix = 'open'
			label = stringaMultilingua('Aperto', 'Open', 'Offen')
		} else {
			suffix = 'closed'
			label = stringaMultilingua('Chiuso', 'Closed', 'Geschlossen')
		}

		if (suffix) {
			cssClass = `asitbh__status--${suffix}`
		}

		return {cssClass, label}
	}

	function formatChange(change) {
		const hasHours = change && change.indexOf(':') !== -1

		change = DateTime.fromISO(change, {zone: 'Europe/Rome', locale: locale.value})

		if (!change.isValid) {
			return
		}

		const daysDiff = change.startOf('day').diff(now.startOf('day'), 'days').days
		let date

		if (daysDiff < 1) {
			date = ''
		} else if (daysDiff < 2) {
			date = change.toRelativeCalendar({
				base: now,
				unit: 'days',
			})
		} else if (daysDiff < 7) {
			date = change.toFormat('ccc')
		} else {
			date = change.toFormat('ccc DD')
		}

		return hasHours ? `${date} alle ore ${formatHour(change)}` : date
	}

	function formatHour(hour) {
		return DateTime.fromISO(hour, {zone: 'Europe/Rome', locale: locale.value}).toFormat('H:mm')
	}

	function formatTableHours(hours) {
		return hours.map(hour => `${formatHour(hour.opensAt)}–${formatHour(hour.closesAt)}`).join(', ')
	}

	function stringaMultilingua(...params) {
		switch (language.value) {
			case 'it':
				return params[0]
			case 'de':
				return params[2]
			default:
				// 2
				return params[1]
		}
	}

	function goToAnchor() {
		document.getElementById('asitmifo-box').scrollIntoView()
	}
</script>
